import React from 'react';

import {
  type CaraAttachment,
  type CaraFileAttachment,
  fileAttachment,
  mergeAttachmentsById,
  replaceAmsAttachmentsFromPickerConfirm
} from '~/components/cara-input/cara-attachment';
import type { AmsPickerConfirmResult } from '~/components/ams-picker';

/**
 * Owns the list of attachments queued on the Cara compose input. Provides
 * imperative helpers for adding/removing individual entries plus a typed
 * batch helper for the most common case (files coming from a drop or a
 * native file picker).
 *
 * The hook is intentionally unopinionated about which attachment kinds are
 * in use — callers decide that by passing the matching union member to
 * `add` / `addFiles`.
 */
export function useCaraAttachments(args?: {
  /**
   * Fires whenever the list changes (add, remove, clear). Useful for parents
   * that want to clear submit errors as soon as the user mutates the queue.
   */
  onChange?: () => void;
}) {
  const { onChange } = args ?? {};
  const [attachments, setAttachments] = React.useState<CaraAttachment[]>([]);

  // Keep the latest `onChange` in a ref so the returned callbacks stay
  // referentially stable across renders.
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const notify = React.useCallback(() => {
    onChangeRef.current?.();
  }, []);

  const add = React.useCallback(
    (...next: CaraAttachment[]) => {
      if (next.length === 0) return;
      setAttachments((prev) => {
        const merged = mergeAttachmentsById(prev, next);
        return merged.length === prev.length && merged.every((a, i) => a === prev[i])
          ? prev
          : merged;
      });
      notify();
    },
    [notify]
  );

  const addFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const newAttachments: CaraFileAttachment[] = files.map((f) => fileAttachment(f));
      setAttachments((prev) => [...prev, ...newAttachments]);
      notify();
    },
    [notify]
  );

  const replaceFromPickerConfirm = React.useCallback(
    (result: AmsPickerConfirmResult) => {
      setAttachments((prev) => replaceAmsAttachmentsFromPickerConfirm(prev, result));
      notify();
    },
    [notify]
  );

  const remove = React.useCallback(
    (id: string) => {
      setAttachments((prev) => {
        const next = prev.filter((a) => a.id !== id);
        if (next.length === prev.length) return prev;
        return next;
      });
      notify();
    },
    [notify]
  );

  const clear = React.useCallback(() => {
    setAttachments((prev) => {
      if (prev.length === 0) return prev;
      return [];
    });
    notify();
  }, [notify]);

  return {
    attachments,
    add,
    addFiles,
    replaceFromPickerConfirm,
    remove,
    clear
  };
}
